import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { logout, setNavOrder } from "../redux/authSlice";
import Select from "react-select";
import {
  FiBarChart2, FiBookOpen, FiCheckCircle, FiGift, FiHelpCircle, FiGrid, FiHome, FiList, FiLogOut, FiMap, FiMessageCircle,
  FiMove, FiPercent, FiSettings, FiShare2, FiSquare, FiStar, FiTag, FiUsers,
} from "react-icons/fi";

import ProductsPanel      from "../components/dashboard/productsPanel";
import DimensionsPanel    from "../components/dashboard/dimensionsPanel";
import UsersPanel         from "../components/dashboard/usersPanel";
import OrdersPanel        from "../components/dashboard/ordersPanel";
import SpecialOrdersPanel from "../components/dashboard/specialOrdersPanel";
import DeliveryPanel      from "../components/dashboard/deliveryPanel";
import PromoPanel         from "../components/dashboard/promoPanel";
import ReviewsPanel       from "../components/dashboard/reviewsPanel";
import FeaturesPanel          from "../components/dashboard/featuresPanel";
import SalesChannelsPanel    from "../components/dashboard/salesChannelsPanel";
import CategoriesPanel       from "../components/dashboard/categoriesPanel";
import StatisticsPanel       from "../components/dashboard/statisticsPanel";
import StoriesPanel          from "../components/dashboard/storiesPanel";
import TestimonialsPanel     from "../components/dashboard/testimonialsPanel";
import CustomerHomesPanel    from "../components/dashboard/customerHomesPanel";
import OfferPanel            from "../components/dashboard/offerPanel";
import FaqsPanel             from "../components/dashboard/faqsPanel";
import NavOrderModal         from "../components/dashboard/navOrderModal";

import styles from "../styles/dashboard.module.css";

const NAV_ITEMS = [
  { id: "products",      label: "اللوحات",       icon: FiGrid },
  { id: "categories",    label: "التصنيفات",      icon: FiTag },
  { id: "stories",       label: "قصص التصاميم",   icon: FiBookOpen },
  { id: "customerhomes", label: "بيوت عملائنا",   icon: FiHome },
  { id: "dimensions",    label: "المقاسات",       icon: FiSquare },
  { id: "users",         label: "العملاء",        icon: FiUsers },
  { id: "orders",        label: "الطلبات",        icon: FiList },
  { id: "specialorders", label: "الطلبات الخاصة", icon: FiCheckCircle },
  { id: "delivery",      label: "مناطق التوصيل",  icon: FiMap },
  { id: "promocodes",    label: "اكواد الخصم",    icon: FiPercent },
  { id: "offer",         label: "عرض الترحيب",    icon: FiGift },
  { id: "faqs",          label: "الأسئلة الشائعة", icon: FiHelpCircle },
  { id: "reviews",       label: "التقييمات",      icon: FiStar },
  { id: "testimonials",  label: "آراء العملاء",   icon: FiMessageCircle },
  { id: "features",      label: "إعدادات خاصة",   icon: FiSettings },
  { id: "channels",    label: "قنوات البيع",  icon: FiShare2 },
  { id: "statistics",  label: "الإحصائيات",   icon: FiBarChart2 },
];

const PANELS = {
  products:      ProductsPanel,
  categories:    CategoriesPanel,
  stories:       StoriesPanel,
  customerhomes: CustomerHomesPanel,
  dimensions:    DimensionsPanel,
  users:         UsersPanel,
  orders:        OrdersPanel,
  specialorders: SpecialOrdersPanel,
  delivery:      DeliveryPanel,
  promocodes:    PromoPanel,
  offer:         OfferPanel,
  faqs:          FaqsPanel,
  reviews:       ReviewsPanel,
  testimonials:  TestimonialsPanel,
  features:      FeaturesPanel,
  channels:      SalesChannelsPanel,
  statistics:    StatisticsPanel,
};

// Apply the admin's saved order (JSON array of ids); unknown ids are dropped and
// panels added later are appended at the end so they never disappear.
const orderNavItems = (saved) => {
  let ids = [];
  try { ids = saved ? JSON.parse(saved) : []; } catch { ids = []; }
  if (!Array.isArray(ids)) ids = [];
  const byId = new Map(NAV_ITEMS.map((item) => [item.id, item]));
  const ordered = ids.map((id) => byId.get(id)).filter(Boolean);
  const seen = new Set(ordered.map((item) => item.id));
  return [...ordered, ...NAV_ITEMS.filter((item) => !seen.has(item.id))];
};

const Dashboard = () => {
  const savedOrder = useSelector((state) => state.auth.user?.dashboardNavOrder);
  const navItems   = useMemo(() => orderNavItems(savedOrder), [savedOrder]);
  const [activeTab, setActiveTab]           = useState(() => navItems[0].id);
  const [orderOpen, setOrderOpen]           = useState(false);
  const [panelKey, setPanelKey]             = useState(0);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const sidebarRef = useRef(null);
  const btnRefs    = useRef([]);
  const dispatch   = useDispatch();
  const navigate   = useNavigate();

  // Load the latest saved order from the server (it may have changed on another device)
  useEffect(() => {
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh-token`)
      .then((res) => {
        const order = res.data?.user?.dashboardNavOrder;
        if (order !== undefined) dispatch(setNavOrder(order));
      })
      .catch(() => {});
  }, [dispatch]);

  const saveNavOrder = async (order) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/dashboard-nav-order`,
        { order }
      );
      dispatch(setNavOrder(res.data?.data?.dashboardNavOrder ?? null));
      setOrderOpen(false);
      toast.success(<span className="toast">تم حفظ ترتيب القائمة</span>);
    } catch {
      toast.error(<span className="toast">فشل حفظ الترتيب. حاول مرة أخرى</span>);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout()).unwrap();
    navigate("/");
  };

  const handleTabChange = useCallback((tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    setPanelKey(k => k + 1);
  }, [activeTab]);

  // Ripple + tab change
  const handleClick = useCallback((e, tabId) => {
    const btn = e.currentTarget;
    const ripple = document.createElement("span");
    ripple.className = styles.ripple;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.cssText = `
      width:${size}px;height:${size}px;
      left:${e.clientX - rect.left - size / 2}px;
      top:${e.clientY - rect.top - size / 2}px;
    `;
    btn.appendChild(ripple);
    setTimeout(() => ripple.remove(), 700);
    handleTabChange(tabId);
  }, [handleTabChange]);

  // Sliding indicator (desktop only)
  useEffect(() => {
    const idx = navItems.findIndex(item => item.id === activeTab);
    const btn     = btnRefs.current[idx];
    const sidebar = sidebarRef.current;
    if (!btn || !sidebar) return;

    const sRect = sidebar.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    const inset = bRect.height * 0.18;
    setIndicatorStyle({
      top:    bRect.top - sRect.top + inset,
      height: bRect.height * 0.64,
    });
  }, [activeTab, navItems]);

  // Scroll-aware nav collapse
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y    = window.scrollY;
      const down = y > lastY;
      document.querySelector(".dashSelect")?.classList.toggle("move", down);
      document.querySelector(".dashbuttons")?.classList.toggle("move", down);
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const selectOptions = [
    { value: "home", label: <><FiHome className="dashicon" /> الصفحة الرئيسية</> },
    ...navItems.map(item => ({
      value: item.id,
      label: <><item.icon className="dashicon" /> {item.label}</>,
    })),
    { value: "reorder", label: <><FiMove className="dashicon" /> ترتيب القائمة</> },
  ];

  const customSelectStyles = {
    control:     (p) => ({ ...p, borderColor: "#e1ae8b", borderWidth: "0.2vw" }),
    singleValue: (p) => ({ ...p, fontSize: "1rem", paddingRight: "13vw", display: "flex", alignItems: "center" }),
    option:      (p, s) => ({
      ...p,
      color:      s.isSelected ? "#e1ae8b" : "#9B773D",
      fontWeight: s.isSelected ? "bold" : "normal",
      paddingRight: "15vw",
      display: "flex",
      alignItems: "center",
    }),
    menuList: (p) => ({ ...p, overflowY: "visible" }),
  };

  const ActivePanel = PANELS[activeTab] || ProductsPanel;

  return (
    <div className={styles.dashRoot}>
      {/* Mobile dropdown */}
      <Select
        options={selectOptions}
        value={selectOptions.find(opt => opt.value === activeTab)}
        onChange={opt => {
          if (opt.value === "home") navigate("/");
          else if (opt.value === "reorder") setOrderOpen(true);
          else handleTabChange(opt.value);
        }}
        styles={customSelectStyles}
        className="dashSelect"
        classNamePrefix="react-select"
        isSearchable={false}
      />

      {/* Sidebar / Tab Bar */}
      <nav ref={sidebarRef} className={`dashbuttons ${styles.sideNav}`}>
        <div className={styles.indicator} style={indicatorStyle} aria-hidden="true" />

        {navItems.map((item, i) => {
          const isActive = item.id === activeTab;
          return (
            <button
              key={item.id}
              ref={el => (btnRefs.current[i] = el)}
              onClick={e => handleClick(e, item.id)}
              className={`${styles.navBtn}${isActive ? ` active ${styles.active}` : ""}`}
            >
              <item.icon
                className={`${styles.navIcon}${isActive ? ` ${styles.navIconActive}` : ""}`}
              />
              <span className={styles.navLabel}>{item.label}</span>
            </button>
          );
        })}

        <div className={styles.signoutDivider} />
        <button className={styles.navBtn} onClick={() => setOrderOpen(true)}>
          <FiMove className={styles.navIcon} />
          <span className={styles.navLabel}>ترتيب القائمة</span>
        </button>
        <button className={styles.navBtn} onClick={() => navigate("/")}>
          <FiHome className={styles.navIcon} />
          <span className={styles.navLabel}>الصفحة الرئيسية</span>
        </button>
        <button className={`${styles.navBtn} ${styles.signoutBtn}`} onClick={handleLogout}>
          <FiLogOut className={`${styles.navIcon} ${styles.signoutIcon}`} />
          <span className={styles.navLabel}>خروج</span>
        </button>
      </nav>

      <NavOrderModal
        isOpen={orderOpen}
        items={navItems}
        defaultItems={NAV_ITEMS}
        onClose={() => setOrderOpen(false)}
        onSave={saveNavOrder}
      />

      {/* Animated panel content */}
      <div key={panelKey} className={styles.panelAnimate}>
        <ActivePanel />
      </div>
    </div>
  );
};

export default Dashboard;
